import { formatCurrency, formatDate, formatString, MONTHS, showDateSelector } from "./functions.js";
import { DashboardResultLine } from "./server-types/DashboardResultLine.js";
import { app } from "./session.js";

interface DashboardRow extends DashboardResultLine {
    status: number;
    statusColor: string;
    forecastAccuracy: number;
    fcColor: string;
};

function dashboard_changed() {

    const yearInput = document.getElementById("year-input");
    const monthInput = document.getElementById("month-input");

    if (yearInput instanceof HTMLSelectElement && monthInput instanceof HTMLSelectElement) {
        let year = parseInt(yearInput.value);
        let month = parseInt(monthInput.value);
        dashboard_show_table(year, month);
    }
    else {
        throw new Error("Could not retrieve year/month input");
    }
}

function createDashboardRow(month: DashboardResultLine): DashboardRow {
    let result: DashboardRow = {
        status: 0,
        forecastAccuracy: 0,
        statusColor: "#FF6666",
        fcColor: "#FF6666",

        id: month.id,
        userName: month.userName,
        userId: month.userId,
        month: month.month,
        year: month.year,
        planName: month.planName,
        annualGoal: month.annualGoal,
        forecast: month.forecast,
        goal: month.goal,
        monthToDate: month.monthToDate,
        notes: month.notes
    };

    const status = ((result.monthToDate / result.goal) * 100);
    result.status = status;
    if (status > 80) result.statusColor = "#FF9216";
    if (status > 90) result.statusColor = "#5FFB17";

    const forecastAccuracy = ((result.monthToDate / result.forecast) * 100);
    result.forecastAccuracy = forecastAccuracy;
    if (forecastAccuracy > 90) result.fcColor = "#5FFB17";

    return result;
}

export async function showDashboard(selectedYear?: number, selectedMonth?: number) {
    const currentDate = new Date();
    // Future date is 1 week in the future
    const futureDate = new Date(currentDate);
    futureDate.setDate(futureDate.getDate() + 15);

    selectedYear = selectedYear ?? currentDate.getFullYear();
    selectedMonth = selectedMonth ?? currentDate.getMonth();
    const maxYear = futureDate.getFullYear() + 2;

    let html = `<h2>Team Dashboard</h2>`;


    (window as any).dashboard_changed = dashboard_changed;
    html += showDateSelector({
        callBack: 'dashboard_changed',
        year: {
            max: maxYear,
            start: 2025,
            selected: selectedYear
        },
        month: { selected: selectedMonth }
    });

    html += `<div id="dashboard-table"></div>`;
    const contentElement = document.getElementById('content');
    if (contentElement) {
        contentElement.innerHTML = html;
    }

    dashboard_show_table(selectedYear, selectedMonth);
}

async function dashboard_show_table(selectedYear: number, selectedMonth: number) {
    const target = document.getElementById('dashboard-table');
    if (target)
        target.innerHTML = "Loading...";

    const members = await app.API.dashboard(selectedYear, selectedMonth);

    let html = `
            <div style="overflow-x:auto;">
            <table style="width: 100%; padding: 30px;">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Full Year Sales Plan</th>
                        <th>Monthly Plan</th>
                        <th>${MONTHS[selectedMonth]} Forecast</th>
                        <th>Status</th>
                        <th>${MONTHS[selectedMonth]} Month-to-Date</th>
                        <th>Forecast Accuracy</th>
                        <th>Notes</th>
                    </tr>
                </thead>`;

    // Totals used to generate the footer
    const totals = {
        count: 0,
        fullSalesPlan: 0,
        monthlyPlan: 0,
        forecast: 0,
        status: 0,
        monthToDate: 0,
        forecastAccuracy: 0
    };

    html += `<tbody>`;
    members
        .map(member => createDashboardRow(member))
        .sort((a, b) => {
            return b.status - a.status;
        })
        .forEach(member => {
            totals.count++;
            totals.fullSalesPlan += member.annualGoal;

            if (member.goal == 0) return;

            totals.monthlyPlan += member.goal;
            totals.forecast += member.forecast;
            totals.monthToDate += member.monthToDate;

            html += `<tr>
                    <td style="white-space: nowrap">${formatString(member.userName + (member.planName ? ` (${member.planName})` : ``))}</td>
                    <td class="numeric-cell">${formatCurrency(member.annualGoal)}</td>
                    <td class="numeric-cell">${formatCurrency(member.goal)}</td>
                    <td class="numeric-cell">${formatCurrency(member.forecast)}</td>
                    <td class="numeric-cell" style="background-color: ${member.statusColor}">${member.status.toFixed(1)}%</td>
                    <td class="numeric-cell">${formatCurrency(member.monthToDate)}</td>
                    <td class="numeric-cell" style="background-color: ${member.fcColor}">${member.forecastAccuracy.toFixed(1)}%</td>
                    <td class="notes-cell"><div class="notes-cell" style="height: 4rem; overflow-y: scroll;"><ul>`;

            for (let note of member.notes) {
                html += `<li><strong>${formatDate(note.time)}</strong> - ${formatString(note.message)}</li>`;
            }

            html += `</ul></div></td></tr>`;
        });
    html += `</tbody>`;

    html += `
        <tfoot>
            <tr>
                <th colspan="8" style="text-align: center;">Totals/Averages</th>
            </tr>
            <tr>
                <th></th>
                <th>Full Year Sales Plan Total</th>
                <th>Monthly Plan Total</th>
                <th>Forecast Total</th>
                <th>Status</th>
                <th>Month-to-Date Total</th>
                <th>Forecast Accuracy</th>
                <th></th>
            </tr>
            <tr>
                <td><!--Name--></td>
                <td class="numeric-cell">${formatCurrency(totals.fullSalesPlan)}</td>
                <td class="numeric-cell">${formatCurrency(totals.monthlyPlan)}</td>
                <td class="numeric-cell">${formatCurrency(totals.forecast)}</td>
                <td class="numeric-cell">${(totals.monthToDate / totals.monthlyPlan * 100).toFixed(1)}%</td>
                <td class="numeric-cell">${formatCurrency(totals.monthToDate)}</td>
                <td class="numeric-cell">${(totals.monthToDate / totals.forecast * 100).toFixed(1)}%</td>
                <td><!--Notes--></td>
            </tr>
        </tfoot>`;

    html += `</table>`;

    if (target)
        target.innerHTML = html;
    else
        throw new Error("No dashboard table element.");
}
